import React from "react";
import MenuElement, {
  IMenuElement,
} from "src/components/side-menu/sub-elements/MenuElement";
import ArrowIcon from "src/components/icons/ArrowIcon";
import {IMenuItem, SHOW_UPPER_ELEMENT_ON_MOBILE } from "src/components/side-menu/SideMenu";
import HeightAnimation from "src/components/layout/HeightAnimation";
import {
  useI18next,
  Link,
} from "gatsby-plugin-react-i18next";
import { registerSideMenuNavigationEvent } from "src/services/GoogleTagManager";
import clsx from "clsx";
import { 
  openCategory, 
  setIsMenuOpen, 
  useOpenCategoryId,
  useCloseSubcategory, 
  useOpenSubcategory, 
  useOpenSubcategoryId, 
  useSelectedSubcategoryId, 
  useIsAlphabetical,
  useSearchQuery
} from "../side-menu-store";
import { useIsLargeScreen, useIsMobile } from "src/helpers/ScreenSizeContext";

export interface ISubCategory extends IMenuItem {
  hideCategoryOnAlphabetical?: boolean
  alphabeticalCategoryNameAsc?: string
  alphabeticalCategoryNameDesc?: string
  alphabeticalName?: string
  link?: string
  elements?: IMenuElement[]
}

interface ISubCategoryProps {
  subCategory: ISubCategory
  hideArrow: boolean
  pdfByCategoryPath?: string | null
  pdfAzPath?: string | null
}

const SubCategory = ({
  subCategory,
  hideArrow,
  pdfByCategoryPath,
  pdfAzPath,
}: ISubCategoryProps) => {
  const isSelected = useSelectedSubcategoryId() === subCategory.id
  const openSubcategoryId = useOpenSubcategoryId()
  const isAlphabetical = useIsAlphabetical()
  const searchQuery = useSearchQuery()
  const openCategoryId = useOpenCategoryId()
  const openSubcategory = useOpenSubcategory()
  const closeSubcategory = useCloseSubcategory()
  const isLarge = useIsLargeScreen()
  const isMobile = useIsMobile()
  const { navigate } = useI18next();

  if ((subCategory.id === 'pdf-by-category' && !pdfByCategoryPath) || (subCategory.id === 'pdf-a-z' && !pdfAzPath)) {
    return null
  }

  const isOpen = openSubcategoryId === subCategory.id
  const isAnotherSubcategoryOpen = !!openSubcategoryId && !isOpen
  const allOpen = isAlphabetical || !!searchQuery

  const handleClick = () => {
    if (subCategory.id === 'pdf-by-category') {
      registerSideMenuNavigationEvent("pdf-by-category");
      window.open(`/pdf${pdfByCategoryPath}`)
    } else if (subCategory.id === 'pdf-a-z') {
      registerSideMenuNavigationEvent("pdf-a-z");
      window.open(`/pdf${pdfAzPath}`)
    } else if (subCategory.link) {
      if (openCategoryId) { // force open to prevent temporary closing of category until new page loads
        openCategory(openCategoryId)
      }
      if (isMobile) {
        setIsMenuOpen(false)
      }
      registerSideMenuNavigationEvent(subCategory.link)
      navigate(subCategory.link)
    } else if (isOpen) {
      closeSubcategory()
    } else if (subCategory.elements?.length) {
      openSubcategory(subCategory.id)
    }
  }

  const linkContent = (
    <>
      {isAlphabetical 
        ? (subCategory.alphabeticalName || subCategory.name) 
        : subCategory.name
      }
      {!subCategory.link && !!subCategory.elements?.length && !hideArrow && (
        <div>
          <ArrowIcon
            rotate={isOpen ? "top" : "bottom"}
            color='appGreen'
          />
        </div>
      )}
    </>
  );

  const SubcategoryLink = () => {
    if (subCategory.elements || !subCategory.link) {
      return (
        <button
          type='button'
          onClick={handleClick}
          onKeyPress={handleClick}
          tabIndex={0}
          className={clsx(
            'cursor-pointer text-sm uppercase py-2.5 px-4 text-left border-t border-appGreen flex justify-between items-center w-full lg:flex bg-appSubCategory lg:bg-appSubCategory leading-1',
            isSelected ? "text-appGreen font-bold" : "text-appGreen",
          )}
        >
          {linkContent}
        </button>
      );
    }

    return (
      <Link
        onClick={handleClick}
        onKeyPress={handleClick}
        to={subCategory.link}
        tabIndex={0}
        className={clsx(
          'cursor-pointer text-sm uppercase py-2.5 px-4 text-left border-t border-appGreen flex justify-between items-center w-full lg:flex bg-appSubCategory lg:bg-appSubCategory leading-1',
          isSelected ? "text-appGreen font-bold" : "text-appGreen",
        )}
        activeClassName="lg:font-bold text-appGreen active-page"
      >
        {linkContent}
      </Link>
    );
  };

  let isVisible
  if (isLarge || isAlphabetical) {
    isVisible = true
  } else if (isAnotherSubcategoryOpen) {
    isVisible =  false
  } else if (isOpen) {
    isVisible = SHOW_UPPER_ELEMENT_ON_MOBILE
  } else {
    isVisible = true
  }

  return (
    <li id={subCategory.id} style={{ marginTop: "-1px" }}>
      <HeightAnimation isOpen={isVisible}>
        {SubcategoryLink()}
      </HeightAnimation>
      <HeightAnimation isOpen={!subCategory.link && !!subCategory.elements?.length && (isOpen || allOpen)}>
        <ul
          className="bg-appElementBackground lg:bg-appElementBackground py-2.5">
          {subCategory.elements?.map((element) => (
            <MenuElement menuElement={element} key={element.id} />
          ))}
        </ul>
      </HeightAnimation>
    </li>
  );
};

export default SubCategory;
